import * as _ from 'lodash';
import { getLogger } from '../diagnostics/logger';
import moment from 'moment';
import { Action } from '../actions';
import {
  CANCEL_SIM_SUBMISSION_SUCCESS,
  CHANGE_SIM_SUBMISSIONS_SELECTED,
  CLEAR_SIM_SUBMISSIONS,
  COMPLETE_SIM_SUBMISSION_SUCCESS,
  LOAD_SELECTED_SIM_SUBMISSION_SUCCESS,
  LOAD_SIM_SUBMISSIONS,
  LOAD_SIM_SUBMISSIONS_FAILURE,
  LOAD_SIM_SUBMISSIONS_SUCCESS,
} from '../actions/actionTypes';
import { HashMap } from '../constants';
import { GridSelection } from '../models';
import { BasicPageableSearchFilter, ComplexSearchFilter } from '../models/searchFilter';
import { LoadSimsFailurePayload, Sim } from '../models/sim';
import { LoadSimSubmissionsSuccessPayload } from '../models/simSubmission';
import { initialAppState } from '../models/state/appState';
import { SimsCacheTimeout, SimsState } from '../models/state/simsState';
import { getDefaultSubmissionsState, SubmissionsState } from '../models/state/submissionsState';
import { SimSubmission } from '../models/transfer/input/SimSubmission';
const log = getLogger('simManagement.reducers.submissionsReducer');

/**
 * Orders an array of sims by name, ignoring case.
 * @param list
 */
function orderSimList(list: Sim[]): Sim[] {
  return _.orderBy(list, [(d: Sim) => d.raNumber], ['asc']);
}

export default function submissionsReducer(
  state: SubmissionsState = initialAppState.submissions,
  action: Action<any>,
): SubmissionsState {
  function getEmptyResults(): Partial<SubmissionsState> {
    return {
      all: { result: [] },
      submissionsByTicketNumber: {},
      lastLoadedOn: undefined,
      expiresOn: undefined,
    };
  }

  switch (action.type) {
    // update the relevant entry in the list to keep it consistent
    case CANCEL_SIM_SUBMISSION_SUCCESS:
    case COMPLETE_SIM_SUBMISSION_SUCCESS:
    case LOAD_SELECTED_SIM_SUBMISSION_SUCCESS: {
      const { payload } = action as Action<SimSubmission>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const updatedSubmission = Object.assign({}, payload, {
        // list items contain the top level object only - commands are not loaded
        commands: [],
      });

      const result = state.all.result.map((submission: any) => {
        return submission.ticketNumber === updatedSubmission.ticketNumber ? updatedSubmission : submission;
      });

      const submissionsByTicketNumber = Object.assign({}, ...result.map((a: any) => ({
        [a.ticketNumber]: a
      })));
      const newState: Partial<SubmissionsState> = {
        all: {
          result,
          count: result.length,
          totalCount: state.all.totalCount,
        },
        submissionsByTicketNumber,
      };

      return Object.assign({}, state, newState);
    }
    case CLEAR_SIM_SUBMISSIONS:
      return getDefaultSubmissionsState();
    case CHANGE_SIM_SUBMISSIONS_SELECTED: {
      const typedAction = action as Action<GridSelection<SimSubmission>>;
      const { payload } = typedAction;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      if (!payload.isSelected && (!state.selected || state.selected !== payload.item.ticketNumber)) {
        return state;
      }

      const newState = Object.assign({}, state, {
        selected: payload.isSelected ? payload.item.ticketNumber : undefined,
      });

      return newState;
    }
    case LOAD_SIM_SUBMISSIONS: {
      const searchFilter = action.payload as ComplexSearchFilter;
      if (!searchFilter) {
        log.warn('No payload');
        return state;
      }

      const changedState: Partial<SimsState> = {
        error: undefined,
        isLoading: true,
        searchFilter,
      };

      const clearSearchResults =
        searchFilter.companyId === state.searchFilter.companyId &&
        searchFilter.accountId === state.searchFilter.accountId;
      const changedStateExtra = clearSearchResults ? {} : getEmptyResults();
      const newState = Object.assign({}, state, changedState, changedStateExtra);
      return newState;
    }
    case LOAD_SIM_SUBMISSIONS_FAILURE: {
      const payload = action.payload as LoadSimsFailurePayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const { error, searchFilter } = payload;
      const changedState: Partial<SimsState> = {
        error,
        isLoading: false,
        searchFilter,
      };

      const clearSearchResults =
        searchFilter.companyId === state.searchFilter.companyId &&
        searchFilter.accountId === state.searchFilter.accountId;
      const changedStateExtra = clearSearchResults ? {} : getEmptyResults();
      const newState = Object.assign({}, state, changedState, changedStateExtra);
      return newState;
    }
    case LOAD_SIM_SUBMISSIONS_SUCCESS: {
      const payload = action.payload as LoadSimSubmissionsSuccessPayload;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const { searchFilter, submissions } = payload;
      const { startIndex, stopIndex } = searchFilter;
      const loadedResult = submissions.result;
      const result = startIndex === 0 ? [] : [...state.all.result];
      for (let i = startIndex; i < startIndex + submissions.result.length; i++) {
        result[i] = {
          ...submissions.result[i - startIndex],
        };
      }

      const submissionsByTicketNumber = Object.assign(
        {},
        ...payload.submissions.result.map((a) => ({ [a.ticketNumber]: a })),
      );

      const changedState: Partial<SubmissionsState> = {
        all: {
          count: submissions.count,
          result,
          totalCount: typeof submissions.totalCount === 'undefined' ? state.all.totalCount : submissions.totalCount,
        },
        error: undefined,
        expiresOn: moment().add(SimsCacheTimeout, 'seconds').toDate(),
        isLoading: false,
        lastLoadedOn: new Date(),
        searchFilter,
        submissionsByTicketNumber,
      };

      const newState = Object.assign({}, state, changedState);
      return newState;
    }
    default:
      return state;
  }
}
